/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   3/14/22
 */

import React from "react";
import ViewportEvent, {eventSx} from "./ViewportEvent";
import {Employee, VegaEvent} from "../interfaces/Global";
import {getEventType} from "./event-types";
import {addDays, addHours, addMinutes, differenceInHours, formatDistance, startOfDay} from "date-fns";
import {
	Button,
	Card,
	CardActions,
	CardContent,
	CardHeader, CircularProgress,
	Dialog, DialogActions, DialogContent, DialogContentText,
	DialogTitle,
	Stack,
	Typography, useTheme
} from "@mui/material";
import {useMutation} from "urql";
import {
	DeleteCalendarEventMutation,
	DeleteCalendarEventResponse,
	DeleteCalendarEventVars, UpdateCalendarEventMutation, UpdateCalendarEventResponse, UpdateCalendarEventVars
} from "../graphql/mutations/calendar-events";
import ModelView, {AttributeConfig} from "../components/ModelView";
import Avatar from "../components/Avatar";
import {LoadingButton} from "@mui/lab";
import {format} from "../dates-helper";
import zonedTimeToUtc from "date-fns-tz/esm/zonedTimeToUtc";

export interface VegaEventProps {
	viewportRange: Interval
	rangeResolution: number
	employee: Employee
	event: VegaEvent
	loading?: boolean
	error?: string
	openEditEventDialog: (event: VegaEvent) => void
}

export default function VegaViewportEvent(props: VegaEventProps) {

	const eventType = getEventType(props.event.typeId);
	const theme = useTheme();

	let snapTo: "start" | "end" | undefined = undefined;

	if (props.rangeResolution < 1 && differenceInHours(props.event.endDatetime, props.event.startDatetime) === 4) {
		// special positioning rules for half-day events
		const [employeeStartHour, employeeStartMin] = props.employee.startTime.split(":").map(i => Number(i));
		const [employeeEndHour, employeeEndMin] = props.employee.endTime.split(":").map(i => Number(i));
		const employeeStart = zonedTimeToUtc(addMinutes(addHours(startOfDay(props.event.startDatetime), employeeStartHour), employeeStartMin), props.employee.timeZone);
		const employeeEnd = zonedTimeToUtc(addMinutes(addHours(startOfDay(props.event.endDatetime), employeeEndHour), employeeEndMin), props.employee.timeZone);
		if (employeeStart.getHours() === props.event.startDatetime.getHours() && employeeStart.getMinutes() === props.event.startDatetime.getMinutes()) {
			snapTo = "start";
		} else if (employeeEnd.getHours() === props.event.endDatetime.getHours() && employeeEnd.getMinutes() === props.event.endDatetime.getMinutes()) {
			snapTo = "end";
		}
	}

	return (
		<ViewportEvent
			viewportRange={props.viewportRange}
			rangeResolution={props.rangeResolution}
			normalizeTimezone={props.employee.timeZone}
			event={{
				title: eventType?.name || "Calendar Event",
				typeId: eventType?.id || "AL",
				startDatetime: props.event.startDatetime,
				endDatetime: props.event.endDatetime
			}}
			tooltip={
				<EventTooltip
					event={props.event}
					employee={props.employee}
				/>
			}
			details={
				<EventDetails
					event={props.event}
					employee={props.employee}
					openEditEventDialog={props.openEditEventDialog}
				/>
			}
			viewportIcon={
				props.event.properties?.loading ?
					<CircularProgress size={14} color={"info"} /> :
					eventType?.icon
			}
			snapTo={snapTo}
			getViewportLabel={(width) => {
				if (!eventType) return null;
				let label = eventType.name;
				if (width <= 40) {
					return null;
				} else if (width <= 80) {
					label = eventType.id;
				} else if (width > 200) {
					label += " (" + formatDistance(props.event.endDatetime, props.event.startDatetime) + ")";
				}
				return label;
			}}
			sx={{
				...eventSx(theme, eventType?.parent?.id?.toLowerCase(), !props.event.isConfirmed),
				...eventSx(theme, props.event.typeId?.toLowerCase(), !props.event.isConfirmed),
			}}
		/>
	);

}

interface EventTooltipProps {
	event: VegaEvent
	employee: Employee
}

function EventTooltip({ event, employee }: EventTooltipProps) {

	const eventType = getEventType(event.typeId);
	const diffInHours = differenceInHours(event.endDatetime, event.startDatetime);
	const eStartDate = event.startDatetime;
	const eEndDate = event.endDatetime;

	return (
		<Stack>
			<Stack>{employee.displayName}</Stack>
			<Typography>{eventType?.name ?? "Calendar Event"}</Typography>
			<Stack>Vega Calendar Event</Stack>
			<Stack>{eventType?.parent?.name}</Stack>
			{diffInHours === 24 && <Stack>{format(eStartDate, 'PPPP')}</Stack>}
			{diffInHours !== 24 && !(diffInHours%24) && <>
				<Stack>First Day: {format(eStartDate, 'PPPP')}</Stack>
				<Stack>Last Day: {format(addDays(eEndDate, -1), 'PPPP')}</Stack>
			</>}
			{diffInHours !== 24 && !!(diffInHours%24) && <>
				<Stack>From: {format(eStartDate, 'PPPP p')}</Stack>
				<Stack>To: {format(eEndDate, 'PPPP p')}</Stack>
			</>}
			<Stack>{formatDistance(eStartDate, eEndDate)}</Stack>
			{!event.isConfirmed && <Stack>Not Confirmed</Stack>}
		</Stack>
	)

}

interface EventDetailsProps {
	event: VegaEvent
	employee: Employee
	openEditEventDialog: (event: VegaEvent) => void
}

function EventDetails({ event, employee, openEditEventDialog }: EventDetailsProps) {

	const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = React.useState(false);

	const [deleteResult, deleteEvent] = useMutation<DeleteCalendarEventResponse, DeleteCalendarEventVars>(DeleteCalendarEventMutation);
	const [updateResult, updateEvent] = useMutation<UpdateCalendarEventResponse, UpdateCalendarEventVars>(UpdateCalendarEventMutation);

	const eventType = getEventType(event.typeId);

	const attributes: AttributeConfig<VegaEvent>[] = [
		"id",
		{ label: "Source", value: "Vega Calendar" },
		{ label: "Category", value: eventType?.parent?.name },
		{ attribute: "creationDate", format: "datetime"  },
		{ label: "Created By", value: event.createdByEmployee ? event.createdByEmployee.displayName + " (" + event.createdBy + ")" : event.createdBy },
		{ attribute: "modifiedDate", format: "datetime", hidden: event.creationDate.getTime() === event.modifiedDate.getTime() || event.confirmationDate?.getTime() === event.modifiedDate.getTime() },
		{ label: "Modified By", value: event.modifiedByEmployee ? event.modifiedByEmployee.displayName + " (" + event.modifiedBy + ")" : event.modifiedBy, hidden: event.creationDate.getTime() === event.modifiedDate.getTime() || event.confirmationDate?.getTime() === event.modifiedDate.getTime() },
		{ attribute: "isConfirmed", label: "Confirmed", format: "boolean" },
		{ attribute: "confirmationDate", format: "datetime" , hidden: !event.isConfirmed || (event.confirmationDate?.getTime() === event.creationDate.getTime() && event.confirmationDate?.getTime() === event.modifiedDate.getTime()) },
		{ label: "Confirmed By", value: event.confirmedByEmployee ? event.confirmedByEmployee.displayName + " (" + event.confirmedBy + ")" : event.confirmedBy, hidden: !event.isConfirmed || (event.confirmationDate?.getTime() === event.creationDate.getTime() && event.confirmationDate?.getTime() === event.modifiedDate.getTime()) },
		{ attribute: "startDatetime", format: "datetime", label: "Start Date" },
		{ attribute: "endDatetime", format: "datetime", label: "End Date"  },
		{ label: "Duration", value: formatDistance(event.startDatetime, event.endDatetime) },
		{ label: "Capacity Modifier", value: Math.round(event.capacityModifier*100) + "%" + (event.capacityModifier === 0 ? " (Unavailable)" : (event.capacityModifier === 1 ? " (Available)" : "")) },
		{ label: "Description", value: event.description?.split("\n").map((i,n) => <>{n > 1 && [<br/>, <br/>]}<span key={n}>{i}</span></>), hidden: !event.description }
	];

	return (
		<Card>
			<CardHeader
				avatar={<Avatar id={employee.id as string} />}
				title={eventType?.name}
				subheader={employee.displayName}
			/>
			<CardContent>
				<ModelView
					attributes={attributes}
					model={event}
					labelSx={{
						pr: 1,
						width: 130,
						textAlign: "right"
					}}
					valueSx={{
						maxWidth: 500,
						maxHeight: 200,
						overflow: "auto"
					}}
					nullTypographyProps={{
						variant: "body2"
					}}
					labelTypographyProps={{
						color: "textSecondary",
						variant: "body2"
					}}
					valueTypographyProps={{
						variant: "body2"
					}}
				/>
			</CardContent>
			<CardActions>
				<Stack direction={"row"} justifyContent={"space-between"} sx={{ width: "100%" }}>
					<Stack direction={"row"}>
						<Button
							onClick={ () => { openEditEventDialog?.(event); } }
						>
							Edit
						</Button>
						{!event.isConfirmed &&
							<LoadingButton
								loading={updateResult.fetching}
								onClick={() => updateEvent({ id: event.id, data: { isConfirmed: true }})}
								variant={"text"}
							>
								Confirm
							</LoadingButton>}
					</Stack>
					<Stack>
						<Button onClick={() => setConfirmDeleteDialogOpen(true) }>
							Delete
						</Button>
					</Stack>
				</Stack>
			</CardActions>
			<Dialog
				open={confirmDeleteDialogOpen}
				onClose={() => { setConfirmDeleteDialogOpen(false)}}
			>
				<DialogTitle>
					Confirm delete
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Are you sure you want to delete this event? This operation is irreversible.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmDeleteDialogOpen(false)}>Cancel</Button>
					<LoadingButton
						loading={deleteResult.fetching}
						onClick={() => deleteEvent({ id: event.id })}
						color={"primary"}
						variant={"contained"}
					>
						Delete
					</LoadingButton>
				</DialogActions>
			</Dialog>
		</Card>
	);

}